/**
 * 风险评估矩阵组件
 * 专业尽职调查通用风险评估表格
 */

import React from 'react';
import { Table, Button, Rate, Tag, Input, Select, Form } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { RiskLevel } from '../types/professionalDD';

const { Option } = Select;
const { TextArea } = Input;

export interface RiskItem {
  riskCategory: string;
  riskDescription: string;
  probability: number;
  impact: number;
  riskLevel: RiskLevel;
  mitigationMeasures: string;
  responsibleParty?: string;
  timeline?: string;
}

interface RiskMatrixProps {
  value?: RiskItem[];
  onChange?: (value: RiskItem[]) => void;
  readonly?: boolean;
  riskCategories?: Array<{ value: string; label: string }>;
  showResponsibleParty?: boolean;
  showTimeline?: boolean;
  emptyText?: string;
  addButtonText?: string;
}

const RiskMatrix: React.FC<RiskMatrixProps> = ({
  value = [],
  onChange,
  readonly = false,
  riskCategories = [
    { value: 'legal', label: '法律风险' },
    { value: 'financial', label: '财务风险' },
    { value: 'operational', label: '运营风险' },
    { value: 'market', label: '市场风险' },
    { value: 'reputation', label: '声誉风险' },
    { value: 'compliance', label: '合规风险' },
  ],
  showResponsibleParty = true,
  showTimeline = true,
  emptyText = '暂无风险评估记录，请点击下方按钮添加',
  addButtonText = '添加风险项',
}) => {
  // 计算风险等级
  const calculateRiskLevel = (
    probability: number,
    impact: number
  ): RiskLevel => {
    const score = probability * impact;
    if (score >= 20) return 'critical';
    if (score >= 12) return 'high';
    if (score >= 6) return 'medium';
    return 'low';
  };

  // 获取风险等级颜色
  const getRiskLevelColor = (level: RiskLevel): string => {
    const colors = {
      low: 'green',
      medium: 'orange',
      high: 'red',
      critical: 'purple',
    };
    return colors[level];
  };

  // 获取风险等级文本
  const getRiskLevelText = (level: RiskLevel): string => {
    const texts = {
      low: '低风险',
      medium: '中等风险',
      high: '高风险',
      critical: '极高风险',
    };
    return texts[level];
  };

  // 更新风险项
  const updateRiskItem = (
    index: number,
    field: keyof RiskItem,
    newValue: any
  ) => {
    if (!onChange) return;

    const newRisks = [...value];
    const updatedRisk = { ...newRisks[index], [field]: newValue };

    // 如果更新的是概率或影响，重新计算风险等级
    if (field === 'probability' || field === 'impact') {
      updatedRisk.riskLevel = calculateRiskLevel(
        field === 'probability' ? newValue : updatedRisk.probability,
        field === 'impact' ? newValue : updatedRisk.impact
      );
    }

    newRisks[index] = updatedRisk;
    onChange(newRisks);
  };

  // 删除风险项
  const removeRiskItem = (index: number) => {
    if (!onChange) return;
    const newRisks = [...value];
    newRisks.splice(index, 1);
    onChange(newRisks);
  };

  // 添加风险项
  const addRiskItem = () => {
    if (!onChange) return;
    const newRisk: RiskItem = {
      riskCategory: '',
      riskDescription: '',
      probability: 1,
      impact: 1,
      riskLevel: 'low',
      mitigationMeasures: '',
      ...(showResponsibleParty && { responsibleParty: '' }),
      ...(showTimeline && { timeline: '' }),
    };
    onChange([...value, newRisk]);
  };

  const columns = [
    {
      title: '风险类别',
      dataIndex: 'riskCategory',
      key: 'riskCategory',
      width: 120,
      render: (text: string, record: RiskItem, index: number) => (
        <Select
          value={text}
          onChange={val => updateRiskItem(index, 'riskCategory', val)}
          disabled={readonly}
          placeholder="选择类别"
          style={{ width: '100%' }}
        >
          {riskCategories.map(category => (
            <Option key={category.value} value={category.value}>
              {category.label}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: '风险描述',
      dataIndex: 'riskDescription',
      key: 'riskDescription',
      width: 200,
      render: (text: string, record: RiskItem, index: number) => (
        <TextArea
          value={text}
          onChange={e =>
            updateRiskItem(index, 'riskDescription', e.target.value)
          }
          disabled={readonly}
          placeholder="详细描述风险内容"
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
      ),
    },
    {
      title: '概率',
      dataIndex: 'probability',
      key: 'probability',
      width: 100,
      render: (value: number, record: RiskItem, index: number) => (
        <div style={{ textAlign: 'center' }}>
          <Rate
            count={5}
            value={value}
            onChange={val => updateRiskItem(index, 'probability', val || 1)}
            disabled={readonly}
          />
          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            {value}/5
          </div>
        </div>
      ),
    },
    {
      title: '影响',
      dataIndex: 'impact',
      key: 'impact',
      width: 100,
      render: (value: number, record: RiskItem, index: number) => (
        <div style={{ textAlign: 'center' }}>
          <Rate
            count={5}
            value={value}
            onChange={val => updateRiskItem(index, 'impact', val || 1)}
            disabled={readonly}
          />
          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            {value}/5
          </div>
        </div>
      ),
    },
    {
      title: '风险等级',
      dataIndex: 'riskLevel',
      key: 'riskLevel',
      width: 120,
      render: (level: RiskLevel, record: RiskItem) => {
        const score = record.probability * record.impact;
        return (
          <div style={{ textAlign: 'center' }}>
            <Tag color={getRiskLevelColor(level)}>
              {getRiskLevelText(level)}
            </Tag>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              评分: {score}
            </div>
          </div>
        );
      },
    },
    {
      title: '缓释措施',
      dataIndex: 'mitigationMeasures',
      key: 'mitigationMeasures',
      width: 200,
      render: (text: string, record: RiskItem, index: number) => (
        <TextArea
          value={text}
          onChange={e =>
            updateRiskItem(index, 'mitigationMeasures', e.target.value)
          }
          disabled={readonly}
          placeholder="描述风险缓释和控制措施"
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
      ),
    },
    ...(showResponsibleParty
      ? [
          {
            title: '责任方',
            dataIndex: 'responsibleParty',
            key: 'responsibleParty',
            width: 120,
            render: (text: string, record: RiskItem, index: number) => (
              <Input
                value={text}
                onChange={e =>
                  updateRiskItem(index, 'responsibleParty', e.target.value)
                }
                disabled={readonly}
                placeholder="责任方"
              />
            ),
          },
        ]
      : []),
    ...(showTimeline
      ? [
          {
            title: '时间安排',
            dataIndex: 'timeline',
            key: 'timeline',
            width: 120,
            render: (text: string, record: RiskItem, index: number) => (
              <Input
                value={text}
                onChange={e =>
                  updateRiskItem(index, 'timeline', e.target.value)
                }
                disabled={readonly}
                placeholder="处理时限"
              />
            ),
          },
        ]
      : []),
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (text: any, record: RiskItem, index: number) => (
        <Button
          type="link"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => removeRiskItem(index)}
          disabled={readonly}
        >
          删除
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Table
        columns={columns}
        dataSource={value}
        pagination={false}
        size="small"
        rowKey={(record, index) => `risk-${index}`}
        locale={{ emptyText }}
        scroll={{ x: 'max-content' }}
      />
      {!readonly && (
        <Button
          type="dashed"
          onClick={addRiskItem}
          icon={<PlusOutlined />}
          style={{ width: '100%', marginTop: 8 }}
        >
          {addButtonText}
        </Button>
      )}
    </div>
  );
};

export default RiskMatrix;
